import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, Phone, Copy, FileText, CheckCircle2, AlertCircle, XCircle,
  MapPin, Clock, Calendar, ArrowRight
} from 'lucide-react';
import { useLanguageStore, type Language } from '../store/languageStore';
import { translatePlaceholders } from '../placeholderUtils';
import { useMatchStore, MatchCriteria } from '../store/matchStore';
import { generateCallScript, AIProgram } from '../api/gpt';
import { useNeedStore } from '../store/needStore';

const messages = {
  EN: {
    back: "Back to Results",
    resourceNotFound: "Resource not found",
    backToResults: "Back to Results",
    highMatch: "High Match – You are likely eligible based on your answers",
    mediumMatch: "Medium Match – You may qualify, but some conditions may affect approval",
    lowMatch: "Low Match – You may not qualify for this program",
    whatYouCanDo: "What You Can Do",
    considerApplying: "Consider applying if your circumstances have changed",
    updateInfo: "Update your information",
    viewOther: "View other programs",
    officeHours: "Office Hours",
    location: "Location",
    contact: "Contact",
    nextDate: "Next Available Date",
    immediate: "Immediate",
    readyToCall: "Ready to Call?",
    copied: "Copied!",
    copyScript: "Copy Script",
    callNow: "Call Now",
    generateLetter: "Generate Letter",
    services: "Services Offered",
    eligibility: "Eligibility Requirements",
    languages: "Languages Supported",
    address: "Address",
    website: "Website",
    introduction: "Introduction",
    mainConversation: "Conversation",
    callTips: "Tips for Your Call",
    speakClearly: "Speak clearly and be patient",
    haveDocuments: "Have your documents ready",
    takeNotes: "Take notes during the call",
    askNextSteps: "Ask for next steps and timeline",
    getName: "Get the name of who you spoke with",
    introduceYourself: "Introduce Yourself Section",
    eligibilityRequirements: "Eligibility Requirements",
    applicationProcess: "Application Process",
    timelineForHelp: "Timeline for Receiving Help",
    amountOfAssistance: "Amount of Assistance Available",
    specialRequirements: "Special Requirements or Conditions",
    documentsNeeded: "Documents Needed",
    identification: "Identification",
    identificationDesc: "Government-issued ID",
    incomeProof: "Income Proof",
    incomeProofDesc: "Recent pay stubs or bank statements",
    residenceProof: "Residence Proof",
    residenceProofDesc: "Utility bills or lease agreements",
    emergencyProof: "Emergency Proof",
    emergencyProofDesc: "Health insurance card or emergency contact information",
    documentsTip: "Tip: Make sure your documents are up-to-date and easily accessible",
    generatingScript: "Generating call script...",
    results: {
      title: "Your Program Matches",
      subtitle: "Based on your information, here are the programs that best match your needs.",
      found: "Found {count} programs sorted by best match"
    }
  },
  ES: {
    back: "Volver a Resultados",
    resourceNotFound: "Recurso no encontrado",
    backToResults: "Volver a Resultados",
    highMatch: "Alta Coincidencia – Es probable que sea elegible según sus respuestas",
    mediumMatch: "Coincidencia Media – Puede calificar, pero algunas condiciones pueden afectar la aprobación",
    lowMatch: "Baja Coincidencia – Es posible que no califique para este programa",
    whatYouCanDo: "Qué Puede Hacer",
    considerApplying: "Considere aplicar si sus circunstancias han cambiado",
    updateInfo: "Actualizar su información",
    viewOther: "Ver otros programas",
    officeHours: "Horario de Oficina",
    location: "Ubicación",
    contact: "Contacto",
    nextDate: "Próxima Fecha Disponible",
    immediate: "Inmediato",
    readyToCall: "¿Listo para Llamar?",
    copied: "¡Copiado!",
    copyScript: "Copiar Guión",
    callNow: "Llamar Ahora",
    generateLetter: "Generar Carta",
    services: "Servicios Ofrecidos",
    eligibility: "Requisitos de Elegibilidad",
    languages: "Idiomas Soportados",
    address: "Dirección",
    website: "Sitio Web",
    introduction: "Introducción",
    mainConversation: "Conversación Principal",
    callTips: "Consejos para su Llamada",
    speakClearly: "Hable claramente y sea paciente",
    haveDocuments: "Tenga sus documentos listos",
    takeNotes: "Tome notas durante la llamada",
    askNextSteps: "Pregunte por los próximos pasos y cronograma",
    getName: "Obtenga el nombre de con quien habló",
    introduceYourself: "Introducción",
    eligibilityRequirements: "Requisitos de Elegibilidad",
    applicationProcess: "Proceso de Aplicación",
    timelineForHelp: "Cronograma para Obtener Ayuda",
    amountOfAssistance: "Monto de Asistencia Disponible",
    specialRequirements: "Requisitos Especiales o Condiciones",
    documentsNeeded: "Documentos Necesarios",
    identification: "Identificación",
    identificationDesc: "Documento de identidad emitido por el gobierno",
    incomeProof: "Comprobante de Ingresos",
    incomeProofDesc: "Recibos de pago o extractos bancarios recientes",
    residenceProof: "Comprobante de Residencia",
    residenceProofDesc: "Facturas de servicios públicos o contratos de arrendamiento",
    emergencyProof: "Comprobante de Emergencia",
    emergencyProofDesc: "Tarjeta de seguro de salud o información de contacto de emergencia",
    documentsTip: "Consejo: Asegúrese de que sus documentos estén actualizados y fácilmente accesibles",
    generatingScript: "Generando guión de llamada...",
    results: {
      title: "Tus Programas Coincidentes",
      subtitle: "Según tu información, aquí están los programas que mejor se adaptan a tus necesidades.",
      found: "Se encontraron {count} programas ordenados por mejor coincidencia"
    }
  },
  ZH: {
    back: "返回结果",
    resourceNotFound: "未找到资源",
    backToResults: "返回结果",
    highMatch: "高匹配度 – 根据您的回答，您可能符合资格",
    mediumMatch: "中等匹配度 – 您可能符合条件，但某些条件可能影响批准",
    lowMatch: "低匹配度 – 您可能不符合该计划的资格",
    whatYouCanDo: "您可以做什么",
    considerApplying: "如果您的情况有变化，请考虑申请",
    updateInfo: "更新您的信息",
    viewOther: "查看其他计划",
    officeHours: "办公时间",
    location: "位置",
    contact: "联系方式",
    nextDate: "下一个可用日期",
    immediate: "立即",
    readyToCall: "准备打电话？",
    copied: "已复制！",
    copyScript: "复制脚本",
    callNow: "立即致电",
    generateLetter: "生成信件",
    services: "提供的服务",
    eligibility: "资格要求",
    languages: "支持的语言",
    address: "地址",
    website: "网站",
    introduction: "介绍",
    mainConversation: "主要对话",
    callTips: "通话技巧",
    speakClearly: "说话清晰并保持耐心",
    haveDocuments: "准备好您的文件",
    takeNotes: "通话时做笔记",
    askNextSteps: "询问后续步骤和时间安排",
    getName: "记下与您通话的人的姓名",
    introduceYourself: "自我介绍部分",
    eligibilityRequirements: "资格要求",
    applicationProcess: "申请流程",
    timelineForHelp: "帮助接收时间表",
    amountOfAssistance: "可用帮助金额",
    specialRequirements: "特殊要求或条件",
    documentsNeeded: "所需文件",
    identification: "身份证明",
    identificationDesc: "政府颁发的身份证件",
    incomeProof: "收入证明",
    incomeProofDesc: "最近几周的工资单或银行对账单",
    residenceProof: "居住证明",
    residenceProofDesc: "水电费账单或租赁协议",
    emergencyProof: "紧急证明",
    emergencyProofDesc: "健康保险卡或紧急联系信息",
    documentsTip: "提示：确保您的文件是最新的，并且易于访问",
    generatingScript: "正在生成通话脚本...",
    results: {
      title: "您的计划匹配",
      subtitle: "根据您的信息，这里是与您的需求最匹配的计划。",
      found: "找到 {count} 个按最佳匹配排序的计划"
    }
  },
  VI: {
    back: "Quay lại Kết quả",
    resourceNotFound: "Không tìm thấy nguồn lực",
    backToResults: "Quay lại Kết quả",
    highMatch: "Phù hợp Cao – Bạn có thể đủ điều kiện dựa trên câu trả lời của bạn",
    mediumMatch: "Phù hợp Trung bình – Bạn có thể đủ điều kiện, nhưng một số điều kiện có thể ảnh hưởng đến việc phê duyệt",
    lowMatch: "Phù hợp Thấp – Bạn có thể không đủ điều kiện cho chương trình này",
    whatYouCanDo: "Những Gì Bạn Có Thể Làm",
    considerApplying: "Xem xét đăng ký nếu hoàn cảnh của bạn đã thay đổi",
    updateInfo: "Cập nhật thông tin của bạn",
    viewOther: "Xem các chương trình khác",
    officeHours: "Giờ làm việc",
    location: "Địa điểm",
    contact: "Liên hệ",
    nextDate: "Ngày có sẵn tiếp theo",
    immediate: "Ngay lập tức",
    readyToCall: "Sẵn sàng gọi điện?",
    copied: "Đã sao chép!",
    copyScript: "Sao chép kịch bản",
    callNow: "Gọi ngay",
    generateLetter: "Tạo thư",
    services: "Dịch vụ được cung cấp",
    eligibility: "Yêu cầu đủ điều kiện",
    languages: "Ngôn ngữ được hỗ trợ",
    address: "Địa chỉ",
    website: "Trang web",
    introduction: "Giới thiệu",
    mainConversation: "Cuộc trò chuyện chính",
    callTips: "Mẹo cho cuộc gọi",
    speakClearly: "Nói rõ ràng và kiên nhẫn",
    haveDocuments: "Chuẩn bị tài liệu sẵn sàng",
    takeNotes: "Ghi chú trong khi gọi",
    askNextSteps: "Hỏi về các bước tiếp theo và lịch trình",
    getName: "Lấy tên người bạn đã nói chuyện",
    introduceYourself: "Phần Giới Thiệu",
    eligibilityRequirements: "Yêu Cầu Đủ Điều Kiện",
    applicationProcess: "Quá Trình Ứng Dụng",
    timelineForHelp: "Lịch Trình Nhận Được Sự Giúp Đỡ",
    amountOfAssistance: "Số Tiền Giúp Đỡ Có Sẵn",
    specialRequirements: "Yêu Cầu Đặc Biệt Hoặc Điều Kiện",
    documentsNeeded: "Tài Liệu Cần Thiết",
    identification: "Chứng Minh",
    identificationDesc: "Giấy Tờ Tùy Thân",
    incomeProof: "Chứng Từ Thu Nhập",
    incomeProofDesc: "Chứng Từ Lương Hoặc Tài Khoản Ngân Hàng",
    residenceProof: "Chứng Từ Nơi Ở",
    residenceProofDesc: "Hóa Đơn Tiền Điện, Nước Hoặc Hợp Đồng Thuê",
    emergencyProof: "Chứng Từ Khẩn Cấp",
    emergencyProofDesc: "Thẻ Bảo Hiểm Y Tế Hoặc Thông Tin Liên Hệ Khẩn Cấp",
    documentsTip: "Lưu Ý: Đảm Bảo Tài Liệu Của Bạn Luôn Mới Nhất Và Dễ Truy Cập",
    generatingScript: "Đang tạo kịch bản gọi điện...",
    results: {
      title: "Các Chương trình Phù hợp của bạn",
      subtitle: "Dựa trên thông tin của bạn, đây là các chương trình phù hợp nhất với nhu cầu của bạn.",
      found: "Tìm thấy {count} chương trình được sắp xếp theo mức độ phù hợp nhất"
    }
  },
  FIL: {
    back: "Bumalik sa Resulta",
    resourceNotFound: "Hindi nahanap ang mapagkukunan",
    backToResults: "Bumalik sa Resulta",
    highMatch: "Mataas na Pagkakatugma – Malamang na kwalipikado ka batay sa iyong mga sagot",
    mediumMatch: "Katamtamang Pagkakatugma – Maaari kang maging kwalipikado, ngunit ang ilang kondisyon ay maaaring makaapekto sa pag-apruba",
    lowMatch: "Mababang Pagkakatugma – Maaaring hindi ka kwalipikado para sa programang ito",
    whatYouCanDo: "Ano ang Magagawa Mo",
    considerApplying: "Isaalang-alang ang pag-apply kung nagbago ang iyong mga kalagayan",
    updateInfo: "I-update ang iyong impormasyon",
    viewOther: "Tingnan ang ibang mga programa",
    officeHours: "Mga Oras ng Opisina",
    location: "Lokasyon",
    contact: "Kontak",
    nextDate: "Susunod na Available na Petsa",
    immediate: "Agad",
    readyToCall: "Handa na bang Tumawag?",
    copied: "Nakopya na!",
    copyScript: "Kopyahin ang Script",
    callNow: "Tumawag Ngayon",
    generateLetter: "Gumawa ng Liham",
    services: "Mga Serbisyong Inaalok",
    eligibility: "Mga Kinakailangan sa Kwalipikasyon",
    languages: "Mga Wikang Sinusuportahan",
    address: "Address",
    website: "Website",
    introduction: "Pagpapakilala",
    mainConversation: "Pangunahing Pag-uusap",
    callTips: "Mga Tip para sa Iyong Tawag",
    speakClearly: "Magsalita nang malinaw at maging mapagpasensya",
    haveDocuments: "Maghanda ng iyong mga dokumento",
    takeNotes: "Magtala habang tumatawag",
    askNextSteps: "Magtanong tungkol sa mga susunod na hakbang at timeline",
    getName: "Kunin ang pangalan ng kausap mo",
    introduceYourself: "Seksyon ng Pagpapakilala",
    eligibilityRequirements: "Mga Kinakailangan sa Kwalipikasyon",
    applicationProcess: "Proseso ng Aplikasyon",
    timelineForHelp: "Timeline para sa Pagtanggap ng Tulong",
    amountOfAssistance: "Halaga ng Tulong na Magagamit",
    specialRequirements: "Mga Espesyal na Kinakailangan o Kondisyon",
    documentsNeeded: "Mga Nangangailangan na Dokumento",
    identification: "Pagpapatunay",
    identificationDesc: "Dokumento ng pagpapatunay na ibinigay ng gobierno",
    incomeProof: "Pagpapatunay ng Kita",
    incomeProofDesc: "Mga pay slip o extracto ng bank",
    residenceProof: "Pagpapatunay ng Tirahan",
    residenceProofDesc: "Mga kontrata ng pagbayad ng tubig o ng renta",
    emergencyProof: "Pagpapatunay ng Emergency",
    emergencyProofDesc: "Card ng bersyon ng kalusugan o impormasyon ng kontak ng emergency",
    documentsTip: "Tip: Siguraduhin na ang iyong mga dokumento ay pinakamahusay at madali na ma-access",
    generatingScript: "Gumagawa ng script ng tawag...",
    results: {
      title: "Ang Iyong mga Pagtutugma ng Programa",
      subtitle: "Batay sa iyong impormasyon, narito ang mga programa na pinakamahusay na tumutugma sa iyong mga pangangailangan.",
      found: "Nahanap ang {count} mga programa na inayos ayon sa pinakamahusay na pagkakatugma"
    }
  },
  KO: {
    back: "결과로 돌아가기",
    resourceNotFound: "리소스를 찾을 수 없습니다",
    backToResults: "결과로 돌아가기",
    highMatch: "높은 일치도 – 귀하의 답변을 바탕으로 자격이 있을 가능성이 높습니다",
    mediumMatch: "중간 일치도 – 자격이 있을 수 있지만 일부 조건이 승인에 영향을 줄 수 있습니다",
    lowMatch: "낮은 일치도 – 이 프로그램에 대한 자격이 없을 수 있습니다",
    whatYouCanDo: "할 수 있는 일",
    considerApplying: "상황이 변경된 경우 신청을 고려하세요",
    updateInfo: "정보 업데이트",
    viewOther: "다른 프로그램 보기",
    officeHours: "근무 시간",
    location: "위치",
    contact: "연락처",
    nextDate: "다음 이용 가능 날짜",
    immediate: "즉시",
    readyToCall: "전화할 준비가 되셨나요?",
    copied: "복사됨!",
    copyScript: "스크립트 복사",
    callNow: "지금 전화",
    generateLetter: "편지 생성",
    services: "제공되는 서비스",
    eligibility: "자격 요건",
    languages: "지원되는 언어",
    address: "주소",
    website: "웹사이트",
    introduction: "소개",
    mainConversation: "주요 대화",
    callTips: "전화 팁",
    speakClearly: "명확하게 말하고 인내심을 가지세요",
    haveDocuments: "문서를 준비하세요",
    takeNotes: "전화 중 메모를 하세요",
    askNextSteps: "다음 단계와 일정에 대해 물어보세요",
    getName: "통화한 사람의 이름을 받으세요",
    introduceYourself: "자기소개 섹션",
    eligibilityRequirements: "자격 요건",
    applicationProcess: "신청 절차",
    timelineForHelp: "도움을 받는 시간표",
    amountOfAssistance: "사용 가능한 도움금액",
    specialRequirements: "특별요건 또는 조건",
    documentsNeeded: "필요한 서류",
    identification: "신분증명",
    identificationDesc: "정부가 발급한 신분증",
    incomeProof: "소득증명",
    incomeProofDesc: "급여명세서 또는 은행계좌 추출물",
    residenceProof: "주소증명",
    residenceProofDesc: "수도요금 또는 임대료 합의서",
    emergencyProof: "비상증명",
    emergencyProofDesc: "건강보험카드 또는 비상연락정보",
    documentsTip: "팁: 문서가 최신이고 접근하기 쉬워야 합니다",
    generatingScript: "전화 스크립트 생성 중...",
    results: {
      title: "귀하의 프로그램 매치",
      subtitle: "귀하의 정보를 바탕으로, 귀하의 필요에 가장 잘 맞는 프로그램은 다음과 같습니다.",
      found: "최고 매치 순으로 정렬된 {count}개의 프로그램을 찾았습니다"
    }
  },
  AR: {
    back: "العودة إلى النتائج",
    resourceNotFound: "لم يتم العثور على المورد",
    backToResults: "العودة إلى النتائج",
    highMatch: "تطابق عالي – من المحتمل أن تكون مؤهلاً بناءً على إجاباتك",
    mediumMatch: "تطابق متوسط – قد تكون مؤهلاً، لكن بعض الشروط قد تؤثر على الموافقة",
    lowMatch: "تطابق منخفض – قد لا تكون مؤهلاً لهذا البرنامج",
    whatYouCanDo: "ما يمكنك فعله",
    considerApplying: "فكر في التقديم إذا تغيرت ظروفك",
    updateInfo: "تحديث معلوماتك",
    viewOther: "عرض البرامج الأخرى",
    officeHours: "ساعات العمل",
    location: "الموقع",
    contact: "جهة الاتصال",
    nextDate: "التاريخ المتاح التالي",
    immediate: "فوري",
    readyToCall: "مستعد للاتصال؟",
    copied: "تم النسخ!",
    copyScript: "نسخ النص",
    callNow: "اتصل الآن",
    generateLetter: "إنشاء خطاب",
    services: "الخدمات المقدمة",
    eligibility: "متطلبات الأهلية",
    languages: "اللغات المدعومة",
    address: "العنوان",
    website: "الموقع الإلكتروني",
    introduction: "مقدمة",
    mainConversation: "المحادثة الرئيسية",
    callTips: "نصائح لمكالمتك",
    speakClearly: "تحدث بوضوح وكن صبوراً",
    haveDocuments: "احتفظ بمستنداتك جاهزة",
    takeNotes: "دوّن الملاحظات أثناء المكالمة",
    askNextSteps: "اسأل عن الخطوات التالية والجدول الزمني",
    getName: "احصل على اسم من تحدثت معه",
    introduceYourself: "قسم التعريف بنفسك",
    eligibilityRequirements: "متطلبات الأهلية",
    applicationProcess: "عملية التقديم",
    timelineForHelp: "الجدول الزمني للحصول على المساعدة",
    amountOfAssistance: "مبلغ المساعدة المتاح",
    specialRequirements: "متطلبات خاصة أو شروط",
    documentsNeeded: "المستندات المطلوبة",
    identification: "المعرفة",
    identificationDesc: "الوثيقة المعرفية التي أصدرتها الحكومة",
    incomeProof: "إثبات الدخل",
    incomeProofDesc: "إثبات الدفع أو مستندات البنك",
    residenceProof: "إثبات المسكن",
    residenceProofDesc: "إتفاقيات الدفع للمياه أو الإيجار",
    emergencyProof: "إثبات الطوارئ",
    emergencyProofDesc: "بطاقة التغطية الصحية أو معلومات الاتصال الطوارئ",
    documentsTip: "تلميح: تأكد من أن مستنداتك حديثة وسهلة الوصول إليها",
    generatingScript: "إنشاء نص المكالمة...",
    results: {
      title: "مطابقات برنامجك",
      subtitle: "بناءً على معلوماتك، إليك البرامج التي تتوافق بشكل أفضل مع احتياجاتك.",
      found: "تم العثور على {count} من البرامج مرتبة حسب أفضل تطابق"
    }
  }
};

// Helper function to get translation from multilingual object
const getTranslation = (translationObj: Record<Language, string> | string, language: Language): string => {
  if (typeof translationObj === 'string') {
    return translationObj;
  }
  return translationObj[language] || translationObj['EN'] || '';
};

// Helper function to parse call script into sections
const parseCallScript = (script: string) => {
  if (!script) return [];

  // Split by section titles (lines that are just titles)
  const sections = script.split(/\n(?=Introduce Yourself|Eligibility Requirements|Application Process|Timeline for Receiving Help|Amount of Assistance Available|Special Requirements or Conditions)/).filter(s => s.trim() !== '');

  if (sections.length === 0) {
    return [{ title: 'Script', content: script }];
  }

  return sections.map((section) => {
    const lines = section.trim().split('\n');
    const titleLine = lines.shift() || '';
    const title = titleLine.trim();
    const content = lines.join('\n').trim();
    return { title, content };
  }).filter(s => s.title && s.content);
};

const Details: React.FC = () => {
  const { selectedLanguage } = useLanguageStore();
  const { matchResults } = useMatchStore();
  const { selectedNeed } = useNeedStore();
  const [copied, setCopied] = useState(false);
  const [callScript, setCallScript] = useState<any>(null);
  const [isScriptLoading, setIsScriptLoading] = useState(true);
  const [scriptError, setScriptError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const titleColorMap: { [key: string]: { title: string; border: string; } } = {
    'Introduce Yourself': { title: 'text-sky-700 dark:text-sky-300', border: 'border-sky-500' },
    'Eligibility Requirements': { title: 'text-emerald-700 dark:text-emerald-300', border: 'border-emerald-500' },
    'Application Process': { title: 'text-amber-700 dark:text-amber-300', border: 'border-amber-500' },
    'Timeline for Receiving Help': { title: 'text-rose-700 dark:text-rose-300', border: 'border-rose-500' },
    'Amount of Assistance Available': { title: 'text-indigo-700 dark:text-indigo-300', border: 'border-indigo-500' },
    'Special Requirements or Conditions': { title: 'text-pink-700 dark:text-pink-300', border: 'border-pink-500' },
  };
  const defaultScheme = { title: 'text-gray-700 dark:text-gray-300', border: 'border-gray-500' };

  // Get resourceId from URL parameters
  const searchParams = new URLSearchParams(location.search);
  const resourceId = searchParams.get('resourceId');
  
  // Find the correct program based on resourceId
  const currentProgram = resourceId 
    ? matchResults.find((program: any) => program.id === parseInt(resourceId)) 
    : matchResults[0] || null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleGenerateScript = async () => {
    if (!currentProgram || !currentProgram.aiProgram) return;
    setIsScriptLoading(true);
    setScriptError(null);
    try {
      const script = await generateCallScript(
        getTranslation(currentProgram.aiProgram.name, selectedLanguage),
        selectedNeed || '',
        selectedLanguage
      );
      setCallScript(parseCallScript(script));
    } catch (error) {
      console.error('Failed to generate call script:', error);
      setScriptError("Sorry, we couldn't generate a script at this time. Please try again later.");
    } finally {
      setIsScriptLoading(false);
    }
  };

  useEffect(() => {
    handleGenerateScript();
  }, [currentProgram?.id, selectedLanguage]);

  const handleCopyScript = () => {
    if (!callScript) return;
    const scriptText = callScript.map((section: { title: string; content: string; }) => `${section.title}\n${section.content}`).join('\n\n');
    navigator.clipboard.writeText(scriptText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!currentProgram || !currentProgram.aiProgram) {
    return (
      <div className="min-h-screen py-8">
        <div className="max-w-2xl mx-auto px-4">
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
            <AlertCircle className="w-12 h-12 text-red-500 dark:text-red-400 mx-auto mb-4" />
            <p className="font-medium mb-4">
              {getTranslation(messages[selectedLanguage].resourceNotFound, selectedLanguage)}
            </p>
            <button
              onClick={() => navigate('/results')}
              className="btn-secondary"
            >
              {getTranslation(messages[selectedLanguage].backToResults, selectedLanguage)}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const getMatchColor = (percentage: number) => {
    if (percentage >= 80) return 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20';
    if (percentage >= 60) return 'border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/20';
    return 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20';
  };

  const getMatchIcon = (percentage: number) => {
    if (percentage >= 80) return <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400" />;
    if (percentage >= 60) return <AlertCircle className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />;
    return <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" />;
  };

  const getMatchLabel = (percentage: number) => {
    if (percentage >= 80) return getTranslation(messages[selectedLanguage].highMatch, selectedLanguage);
    if (percentage >= 60) return getTranslation(messages[selectedLanguage].mediumMatch, selectedLanguage);
    return getTranslation(messages[selectedLanguage].lowMatch, selectedLanguage);
  };

  // Format office hours
  const formatOfficeHours = () => {
    if (!currentProgram?.aiProgram?.hours) return 'Monday-Friday, 9:00 AM - 5:00 PM';
    const hours = Object.entries(currentProgram.aiProgram.hours);
    if (hours.length === 0) return 'Monday-Friday, 9:00 AM - 5:00 PM';
    
    return hours.map(([day, time]) => `${day.charAt(0).toUpperCase() + day.slice(1)}: ${time}`).join(' | ');
  };

  // Format languages
  const formatLanguages = () => {
    if (!currentProgram?.aiProgram?.languages || currentProgram.aiProgram.languages.length === 0) return 'English';
    const languageMap: { [key: string]: string } = {
      'en': 'English',
      'es': 'Spanish',
      'zh': 'Chinese',
      'vi': 'Vietnamese',
      'fil': 'Filipino',
      'ko': 'Korean',
      'ar': 'Arabic'
    };
    return currentProgram.aiProgram.languages.map((lang: string) => languageMap[lang] || lang).join(', ');
  };

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="mt-64 sm:mt-48">
          <button onClick={() => navigate('/results')} className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-6">
            <ArrowLeft className="w-5 h-5 mr-2" />
            {getTranslation(messages[selectedLanguage].back, selectedLanguage)}
          </button>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 md:p-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {getTranslation(currentProgram.aiProgram.name, selectedLanguage)}
          </h1>

          {currentProgram && (
            <div className={`rounded-xl p-6 border mb-6 ${getMatchColor(currentProgram.matchPercentage)}`}>
              <div className="flex items-center gap-2 mb-4">
                {getMatchIcon(currentProgram.matchPercentage)}
                <h2 className="text-lg font-semibold">
                  {getMatchLabel(currentProgram.matchPercentage)}
                </h2>
              </div>

              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mb-4">
                <div 
                  className={`h-full rounded-full transition-all duration-500 ${
                    currentProgram.matchPercentage >= 80 ? 'bg-green-500 dark:bg-green-400' :
                    currentProgram.matchPercentage >= 60 ? 'bg-yellow-500 dark:bg-yellow-400' :
                    'bg-red-500 dark:bg-red-400'
                  }`}
                  style={{ width: `${currentProgram.matchPercentage}%` }}
                />
              </div>

              <div className="space-y-3">
                {currentProgram.matchCriteria.map((criteria: MatchCriteria, index: React.Key | null | undefined) => (
                  <div key={index} className="flex items-start gap-2">
                    {criteria.passed ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    )}
                    <p className="text-sm">{getTranslation(criteria.name, selectedLanguage)}</p>
                  </div>
                ))}
              </div>

              {currentProgram.matchPercentage < 60 && (
                <div className="mt-6 pt-6 border-t border-red-200 dark:border-red-800/30">
                  <h3 className="font-semibold mb-3">
                    {getTranslation(messages[selectedLanguage].whatYouCanDo, selectedLanguage)}
                  </h3>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <ArrowRight className="w-4 h-4" />
                      {getTranslation(messages[selectedLanguage].considerApplying, selectedLanguage)}
                    </li>
                    <li className="flex items-center gap-2">
                      <ArrowRight className="w-4 h-4" />
                      <button 
                        onClick={() => navigate('/match')}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                      >
                        {getTranslation(messages[selectedLanguage].updateInfo, selectedLanguage)}
                      </button>
                    </li>
                    <li className="flex items-center gap-2">
                      <ArrowRight className="w-4 h-4" />
                      <button 
                        onClick={() => navigate('/results')}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                      >
                        {getTranslation(messages[selectedLanguage].viewOther, selectedLanguage)}
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          )}

          <p className="mb-6">
            {getTranslation(currentProgram.aiProgram.description, selectedLanguage)}
          </p>

          {/* Services and Eligibility */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div className="space-y-4">
              <h3 className="font-medium mb-3">{getTranslation(messages[selectedLanguage].services, selectedLanguage)}</h3>
              <ul className="space-y-1 text-sm">
                {currentProgram.aiProgram.services.map((service: string, index: number) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-blue-600 dark:text-blue-400">•</span>
                    {getTranslation(service, selectedLanguage)}
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-4">
              <h3 className="font-medium mb-3">{getTranslation(messages[selectedLanguage].eligibility, selectedLanguage)}</h3>
              <ul className="space-y-1 text-sm">
                {currentProgram.aiProgram.eligibility.map((req: string, index: number) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-green-600 dark:text-green-400">•</span>
                    {getTranslation(req, selectedLanguage)}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="font-medium mb-3">
                {getTranslation(messages[selectedLanguage].address, selectedLanguage)}
              </h3>
              <p className="text-sm">
                {getTranslation(currentProgram.aiProgram.address.street, selectedLanguage)}, {getTranslation(currentProgram.aiProgram.address.city, selectedLanguage)}, {getTranslation(currentProgram.aiProgram.address.state, selectedLanguage)} {getTranslation(currentProgram.aiProgram.address.zip, selectedLanguage)}
              </p>
            </div>
            <div>
              <h3 className="font-medium mb-3">
                {getTranslation(messages[selectedLanguage].contact, selectedLanguage)}
              </h3>
              <p className="text-sm">{getTranslation(currentProgram.aiProgram.contact.phone, selectedLanguage)}</p>
              <p className="text-sm text-blue-600 dark:text-blue-400">{getTranslation(currentProgram.aiProgram.contact.email, selectedLanguage)}</p>
            </div>
          </div>

          {/* Documents Needed Section */}
          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-6 mb-4">
            <h4 className="font-medium text-amber-700 dark:text-amber-300 mb-4 flex items-center gap-2 text-lg">
              📋 {getTranslation(messages[selectedLanguage].documentsNeeded, selectedLanguage)}
            </h4>
            
            <div className="bg-amber-100 dark:bg-amber-900/40 rounded-lg p-6">
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <span className="text-amber-600 dark:text-amber-400 font-semibold">•</span>
                  <div>
                    <h5 className="font-medium text-amber-800 dark:text-amber-200 mb-1">
                      {getTranslation(messages[selectedLanguage].identification, selectedLanguage)}
                    </h5>
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      {getTranslation(messages[selectedLanguage].identificationDesc, selectedLanguage)}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <span className="text-amber-600 dark:text-amber-400 font-semibold">•</span>
                  <div>
                    <h5 className="font-medium text-amber-800 dark:text-amber-200 mb-1">
                      {getTranslation(messages[selectedLanguage].incomeProof, selectedLanguage)}
                    </h5>
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      {getTranslation(messages[selectedLanguage].incomeProofDesc, selectedLanguage)}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <span className="text-amber-600 dark:text-amber-400 font-semibold">•</span>
                  <div>
                    <h5 className="font-medium text-amber-800 dark:text-amber-200 mb-1">
                      {getTranslation(messages[selectedLanguage].residenceProof, selectedLanguage)}
                    </h5>
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      {getTranslation(messages[selectedLanguage].residenceProofDesc, selectedLanguage)}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <span className="text-amber-600 dark:text-amber-400 font-semibold">•</span>
                  <div>
                    <h5 className="font-medium text-amber-800 dark:text-amber-200 mb-1">
                      {getTranslation(messages[selectedLanguage].emergencyProof, selectedLanguage)}
                    </h5>
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      {getTranslation(messages[selectedLanguage].emergencyProofDesc, selectedLanguage)}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-amber-200 dark:bg-amber-800/30 rounded-lg">
                <p className="text-sm text-amber-800 dark:text-amber-200 font-medium">
                  💡 {getTranslation(messages[selectedLanguage].documentsTip, selectedLanguage)}
                </p>
              </div>
            </div>
          </div>

          {/* Call Script with organized sections */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 mb-4">
            <h4 className="font-medium text-blue-700 dark:text-blue-300 mb-4 flex items-center gap-2 text-lg">
              📞 {getTranslation(messages[selectedLanguage].readyToCall, selectedLanguage)}
            </h4>
            
            <div className="bg-blue-100 dark:bg-blue-900/40 rounded-lg p-6">
              {isScriptLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-blue-600 dark:text-blue-400">
                    {getTranslation(messages[selectedLanguage].generatingScript, selectedLanguage)}
                  </p>
                </div>
              ) : scriptError ? (
                <div className="text-center py-8">
                  <p className="text-red-600 dark:text-red-400">
                    {scriptError}
                  </p>
                </div>
              ) : callScript ? (
                <div className="space-y-6">
                  {callScript.map((section: { title: string; content: string; }, index: number) => {
                    const scheme = titleColorMap[section.title] || defaultScheme;
                    return (
                      <div key={index} className={`border-l-4 ${scheme.border} pl-4`}>
                        {section.title !== 'Script' && (
                          <h6 className={`text-lg font-semibold ${scheme.title} mb-2`}>
                            {getTranslation(section.title, selectedLanguage)}
                          </h6>
                        )}
                        <p className="text-base text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                          {getTranslation(section.content, selectedLanguage)}
                        </p>
                      </div>
                    );
                  })}
                </div>
              ) : null}
              <div className="mt-6 flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleCopyScript}
                  className="flex items-center justify-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200"
                >
                  <Copy className="w-5 h-5 mr-2" />
                  {copied 
                    ? getTranslation(messages[selectedLanguage].copied, selectedLanguage)
                    : getTranslation(messages[selectedLanguage].copyScript, selectedLanguage)}
                </button>
                <a
                  href={`tel:${currentProgram.aiProgram.contact.phone}`}
                  className="flex items-center justify-center px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-200"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  {getTranslation(messages[selectedLanguage].callNow, selectedLanguage)}
                </a>
              </div>
            </div>
          </div>

          {/* Tips Section */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 mb-6">
            <h4 className="font-medium text-blue-700 dark:text-blue-300 mb-4 flex items-center gap-2 text-lg">
              💡 {getTranslation(messages[selectedLanguage].callTips, selectedLanguage)}
            </h4>
            
            <div className="bg-blue-100 dark:bg-blue-900/40 rounded-lg p-6">
              <ul className="space-y-3 text-sm text-blue-800 dark:text-blue-200">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 dark:text-blue-400 font-semibold">•</span>
                  <span>{getTranslation(messages[selectedLanguage].speakClearly, selectedLanguage)}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 dark:text-blue-400 font-semibold">•</span>
                  <span>{getTranslation(messages[selectedLanguage].haveDocuments, selectedLanguage)}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 dark:text-blue-400 font-semibold">•</span>
                  <span>{getTranslation(messages[selectedLanguage].takeNotes, selectedLanguage)}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 dark:text-blue-400 font-semibold">•</span>
                  <span>{getTranslation(messages[selectedLanguage].askNextSteps, selectedLanguage)}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 dark:text-blue-400 font-semibold">•</span>
                  <span>{getTranslation(messages[selectedLanguage].getName, selectedLanguage)}</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-4 mb-8">
            <button
              onClick={() => navigate('/appeal-letter', { 
                state: { 
                  resource: {
                    name: currentProgram.aiProgram.name,
                    type: currentProgram.type,
                    contact: currentProgram.aiProgram.contact,
                    address: currentProgram.aiProgram.address
                  }
                }
              })}
              className="flex items-center justify-center px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors duration-200"
            >
              <FileText className="w-5 h-5 mr-2" />
              {getTranslation(messages[selectedLanguage].generateLetter, selectedLanguage)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Details;